import { useEffect, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { createPath } from 'react-router';
import { trackMetricsEvent } from '@gonfalon/metrics/analytics';
import { DataSourceSelector, DataSourceSelectorTriggerSlot } from '@gonfalon/metrics/DataSourceSelector';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { useWarehouseExpIntegrations } from '@gonfalon/metrics/MetricDataSources';
import { toCreateDataSourcesMetric } from '@gonfalon/navigator/navigators';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Button, FieldError, ProgressBar, SelectValue, Text } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import { RequiredLabel } from './components/RequiredLabel';
import type { MetricForm } from './formUtils';
import { useWarehouseManifests } from './useWarehouseManifests';

import styles from './MetricDataSourceFields.module.css';

const FIELD_NAME = 'dataSourceKey';

export const MetricDataSourceFields = ({
  control,
  isDisabled,
  showCreateCTA = false,
}: {
  control: Control<MetricForm>;
  onChange?: (dataSourceKey: MetricForm['dataSourceKey']) => void;
  isDisabled?: boolean;
  showCreateCTA?: boolean;
}) => {
  const projectKey = useProjectKey();
  const { integrationConfigs: warehouseExpConfigs } = useWarehouseExpIntegrations({ filter: { projectKey } });
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  const {
    field,
    fieldState: { error },
  } = useController({
    name: FIELD_NAME,
    control,
    rules: { required: 'This field is required' },
    defaultValue: '',
  });
  const { setValue } = useFormContext<MetricForm>();
  const { data, isPending } = useQuery(getMetricDataSourcesQuery({ projKey: projectKey }));
  const warehouseManifests = useWarehouseManifests();
  const dataSources = useMemo(() => {
    const selectedDataSource = data?.items?.find((ds) => ds.key === dataSourceKeyFormValue);
    const selectedWarehouse = warehouseManifests?.find(
      (manifest) => manifest?.key === selectedDataSource?.integrationKey,
    )?.warehouseVendor;

    return (
      data?.items
        // Filtering out data sources belonging to environments which have been deleted.
        // It might make sense to perform this filtering on the backend instead.
        ?.filter?.((ds) => Boolean(ds.environmentKey))
        ?.filter((ds) => {
          if (!dataSourceKeyFormValue) {
            return false;
          }
          if (!isLDHosted(dataSourceKeyFormValue) && selectedWarehouse) {
            return ds.integrationKey === selectedDataSource?.integrationKey;
          } else {
            return true;
          }
        })
    );
  }, [data, dataSourceKeyFormValue, warehouseManifests]);

  useEffect(() => {
    if (
      dataSourceKeyFormValue === undefined &&
      warehouseExpConfigs.length &&
      !isPending &&
      !dataSources?.length &&
      !error
    ) {
      control.setError('dataSourceKey', { message: 'First create a metric data source to measure external events' });
    }
  }, [warehouseExpConfigs, dataSources, error, dataSourceKeyFormValue, isPending]);

  if (isLDHosted(dataSourceKeyFormValue)) {
    return null;
  }

  const handleCreateDataSource = () => {
    trackMetricsEvent('Clicked CTA Create Metric Data Source', { location: 'createMetricModal' });
    const firstWarehouseExpConfig = warehouseExpConfigs[0];

    if (firstWarehouseExpConfig?._integrationKey) {
      window.open(
        createPath(
          toCreateDataSourcesMetric(
            {
              projectKey,
            },
            {
              search: new URLSearchParams({
                integrationKey: firstWarehouseExpConfig._integrationKey,
                projectKey,
              }),
            },
          ),
        ),
        '_blank',
      );
    } else {
      window.open(createPath(toCreateDataSourcesMetric({ projectKey })), '_blank');
    }
  };

  const selectorProps = {
    selectedKey: dataSourceKeyFormValue,
    dataSources: dataSources ?? [],
    showCreateOption: showCreateCTA,
    onCreateOption: handleCreateDataSource,
    onChange: (dataSource: { key: string; integrationKey?: string }) => {
      field.onChange?.(dataSource.key);
      if (!('integrationKey' in dataSource) || dataSource.integrationKey !== 'snowflake-experimentation') {
        setValue('windowMode', 'none');
        setValue('windowStart', undefined);
        setValue('windowEnd', undefined);
      }
    },
    selectProps: {
      isInvalid: Boolean(error),
      placeholder: dataSources?.length ? 'Select data source' : 'No metric data sources found for this project.',
    },
  };
  const triggerContent = (
    <>
      <RequiredLabel label="Metric data source" htmlFor="dataSource" />
      <Button isDisabled={isDisabled || isPending} className={styles.trigger}>
        {isPending ? (
          <>
            <Text className={styles.loadingDataSources}>Loading data sources...</Text>
            <ProgressBar aria-label="Loading data sources" size="small" isIndeterminate />
          </>
        ) : (
          <>
            <SelectValue />
            <Icon name="chevron-down" size="small" />
          </>
        )}
      </Button>
      <FieldError>{error?.message}</FieldError>
    </>
  );

  return (
    <div className={styles.dataSourceSelectorWrapper}>
      <DataSourceSelector dataSourceVariant="metric" {...selectorProps}>
        <DataSourceSelectorTriggerSlot>{triggerContent}</DataSourceSelectorTriggerSlot>
      </DataSourceSelector>
    </div>
  );
};
